import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Send, MessageCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  { code: 'CY', label: 'Cyprus (free shipping)' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'GR', label: 'Greece' },
  { code: 'DE', label: 'Germany' },
  { code: 'FR', label: 'France' },
  { code: 'IT', label: 'Italy' },
  { code: 'ES', label: 'Spain' },
  { code: 'NL', label: 'Netherlands' },
  { code: 'BE', label: 'Belgium' },
  { code: 'IE', label: 'Ireland' },
  { code: 'PT', label: 'Portugal' },
  { code: 'AT', label: 'Austria' },
  { code: 'SE', label: 'Sweden' },
  { code: 'DK', label: 'Denmark' },
  { code: 'FI', label: 'Finland' },
  { code: 'PL', label: 'Poland' },
  { code: 'US', label: 'United States' },
  { code: 'CA', label: 'Canada' },
  { code: 'AU', label: 'Australia' },
  { code: 'NZ', label: 'New Zealand' },
];

/**
 * "Chat first" custom-order modal, triggered from the Bracelet Builder.
 * Captures customer + shipping details up-front so Lindy can send a PayPal
 * invoice with the correct address without a second Messenger round-trip.
 */
export default function CustomOrderModal({
  open,
  onOpenChange,
  customText = '',
  sizeCm = null,
  clasp = 'lobster',
  unitPrice = null,
  builderStats = null,
}) {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    quantity: 1,
    address1: '',
    address2: '',
    city: '',
    postal_code: '',
    country: 'CY',
    special_instructions: '',
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!customText.trim()) {
      toast.error('Type your word or phrase in the builder first');
      return;
    }
    // Required: name + email + shipping address
    const missing = [];
    if (!form.first_name.trim()) missing.push('first name');
    if (!form.last_name.trim()) missing.push('last name');
    if (!form.email.trim()) missing.push('email');
    if (!form.address1.trim()) missing.push('street address');
    if (!form.city.trim()) missing.push('city');
    if (!form.postal_code.trim()) missing.push('postal code');
    if (missing.length) {
      toast.error(`Please add your ${missing.join(', ')}`);
      return;
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!emailOk) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const sizeLine = builderStats?.sizeCm
        ? `Bracelet size: ${builderStats.sizeCm.toFixed(1)} cm · `
        : '';
      const meta = builderStats
        ? `[From Bracelet Builder] ${sizeLine}Total beads: ${builderStats.totalBeads} · Dots: ${builderStats.dots} · Dashes: ${builderStats.dashes} · Cream filler beads: ${builderStats.fillerBeads}\n\n`
        : '';
      const fullName = `${form.first_name.trim()} ${form.last_name.trim()}`.trim();
      const qty = Math.max(1, Number(form.quantity) || 1);
      const { data } = await axios.post(`${API}/orders/custom`, {
        name: fullName,
        email: form.email.trim().toLowerCase(),
        bracelet_style: 'custom',
        clasp: clasp || 'lobster',
        size_cm: sizeCm ?? null,
        quantity: qty,
        special_instructions: `${meta}${form.special_instructions.trim()}`.trim(),
        custom_text: customText.trim(),
        phone: form.phone.trim() || null,
        shipping_first_name: form.first_name.trim(),
        shipping_last_name: form.last_name.trim(),
        shipping_address_line1: form.address1.trim(),
        shipping_address_line2: form.address2.trim() || null,
        shipping_city: form.city.trim(),
        shipping_postal_code: form.postal_code.trim(),
        shipping_country: (form.country || 'CY').toUpperCase(),
      });
      setDone(data);
      toast.success('Request sent — Lindy will be in touch within 24 hours');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send request');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (o) => {
    if (!o) {
      // Reset when closing so opening again feels fresh
      setDone(null);
      setForm({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        quantity: 1,
        address1: '',
        address2: '',
        city: '',
        postal_code: '',
        country: 'CY',
        special_instructions: '',
      });
    }
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="custom-order-modal"
      >
        {done ? (
          <div className="text-center py-6" data-testid="custom-order-success-inline">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#84A98C]/15 flex items-center justify-center mb-4">
              <MessageCircle className="w-6 h-6 text-[#84A98C]" strokeWidth={1.5} />
            </div>
            <h3 className="font-serif text-xl text-[#354F52] mb-2">
              Thank you — I&rsquo;ve got your note
            </h3>
            <p className="text-sm text-[#52796F] leading-relaxed">
              I&rsquo;ll reply to <b>{form.email || 'you'}</b> within 24 hours with a
              mock-up and PayPal invoice. Your reference is{' '}
              <span className="font-mono text-[#354F52]">{done.order_id}</span>.
            </p>
            <Button
              className="btn-secondary mt-6"
              onClick={() => handleClose(false)}
              data-testid="custom-order-close"
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">
                Chat with Lindy first
              </DialogTitle>
              <DialogDescription>
                {customText ? (
                  <>
                    I&rsquo;ll design a mock-up around{' '}
                    <span className="italic text-[#354F52]">&ldquo;{customText}&rdquo;</span>{' '}
                    and reply within 24 hours with a PayPal invoice before you commit to anything.
                  </>
                ) : (
                  <>Type your word or phrase in the builder above, then send.</>
                )}
              </DialogDescription>
            </DialogHeader>

            {unitPrice != null && (
              <div
                className="mt-2 flex items-baseline justify-between border border-[#CAD2C5]/50 bg-[#FAF9F6] rounded-sm px-4 py-3"
                data-testid="custom-modal-price-summary"
              >
                <div className="text-[11px] uppercase tracking-widest text-[#84A98C]">
                  Estimated price
                  {sizeCm ? ` · ${sizeCm.toFixed ? sizeCm.toFixed(1) : sizeCm} cm` : ''}
                  {clasp ? ` · ${clasp.replace(/-/g, ' ')}` : ''}
                </div>
                <div className="text-xl font-serif text-[#D4AF37]">
                  €{(unitPrice * Math.max(1, Number(form.quantity) || 1)).toFixed(2)}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5 mt-2">
              {/* Contact */}
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#84A98C] mb-2">
                  Your details
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">First name *</Label>
                    <Input
                      value={form.first_name}
                      onChange={(e) => set('first_name', e.target.value)}
                      placeholder="Jane"
                      autoComplete="given-name"
                      data-testid="custom-modal-first-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Last name *</Label>
                    <Input
                      value={form.last_name}
                      onChange={(e) => set('last_name', e.target.value)}
                      placeholder="Smith"
                      autoComplete="family-name"
                      data-testid="custom-modal-last-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Email *</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => set('email', e.target.value)}
                      placeholder="you@example.com"
                      autoComplete="email"
                      data-testid="custom-modal-email"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Phone / WhatsApp (optional)</Label>
                    <Input
                      value={form.phone}
                      onChange={(e) => set('phone', e.target.value)}
                      placeholder="+357…"
                      autoComplete="tel"
                      data-testid="custom-modal-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping address */}
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#84A98C] mb-2">
                  Ship to
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Street address *</Label>
                    <Input
                      value={form.address1}
                      onChange={(e) => set('address1', e.target.value)}
                      placeholder="e.g. 12 Odos Aphroditis"
                      autoComplete="address-line1"
                      data-testid="custom-modal-address1"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Apartment / floor (optional)</Label>
                    <Input
                      value={form.address2}
                      onChange={(e) => set('address2', e.target.value)}
                      placeholder="e.g. Flat 3B"
                      autoComplete="address-line2"
                      data-testid="custom-modal-address2"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">City / town *</Label>
                    <Input
                      value={form.city}
                      onChange={(e) => set('city', e.target.value)}
                      placeholder="Paphos"
                      autoComplete="address-level2"
                      data-testid="custom-modal-city"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Postal code *</Label>
                    <Input
                      value={form.postal_code}
                      onChange={(e) => set('postal_code', e.target.value)}
                      placeholder="8010"
                      autoComplete="postal-code"
                      data-testid="custom-modal-postal"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Country *</Label>
                    <select
                      value={form.country}
                      onChange={(e) => set('country', e.target.value)}
                      className="w-full h-10 px-3 py-2 text-sm bg-white text-[#354F52] rounded-md border border-[#CAD2C5] focus:border-[#D4AF37] focus:outline-none"
                      data-testid="custom-modal-country"
                      autoComplete="country"
                    >
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Meta */}
              <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-3">
                <div>
                  <Label className="text-xs">Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    max="50"
                    value={form.quantity}
                    onChange={(e) => set('quantity', e.target.value)}
                    data-testid="custom-modal-qty"
                  />
                </div>
                <div>
                  <Label className="text-xs">Anything else Lindy should know?</Label>
                  <Textarea
                    value={form.special_instructions}
                    onChange={(e) => set('special_instructions', e.target.value)}
                    rows={2}
                    placeholder="Occasion, colour preference, when you need it by…"
                    data-testid="custom-modal-notes"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleClose(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !customText.trim()}
                  className="btn-primary"
                  data-testid="custom-modal-submit"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />
                  ) : (
                    <Send className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  )}
                  Send request
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
